import './TraffickingPage.scss';

import { IconDrag } from '@hulu-react-style-components/icons';
import { Tab, TabNavigation } from '@hulu/react-style-components';
import { lowerCase as _lowerCase } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import type { TableInstance } from 'react-table';

import ClearAllButton from '../../../common/Button/ClearAllButton';
import CustomModal from '../../../common/CustomModal';
import ToggleSwitch from '../../../common/ToggleSwitch';
import TraffickingTable from '../../../common/TraffickingTable';
import { draggedElementId } from '../../../common/TraffickingTable/constants';
import type { TraffickingTableName } from '../../../constants';
import { TRAFFICKING_SEARCH_PARAMS, TRAFFICKING_TABLE_NAMES } from '../../../constants';
import { useFiltersContext } from '../../../contexts/FilterContext';
import { TraffickingTableReloadContext } from '../../../contexts/TraffickingTableReloadContext';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import bem from '../../../utils/bem';
import { AppliedFiltersBar } from '../AppliedFiltersBar';
import SelectedItemsPill from '../SelectedItemsPill';
import TableActionBar from '../TableActionBar';
import TableFilterBar from '../TableFilterBar';
import TableNavigationActionButtons from '../TableNavigationActionButtons';
import TraffickingPageDrawer from '../TraffickingPageDrawer';
import {
  CLEAR_ALL_BUTTON_LABEL,
  CLEAR_SEARCH_RESULTS_AND_SELECTION_BUTTON_TOOLTIP,
  HIDE_UNSELECTED_ROWS_LABEL,
  HIDE_UNSELECTED_ROWS_TOOLTIP,
} from './constants';
import { useTraffickerState } from './hooks';
import type { EntityModel } from './modelConverters';

const [block, element] = bem('trafficking-page');

const emptyTableMessages: { [key: string]: React.ReactNode } = {
  campaigns: 'No campaigns found matching your filters, selections and groupings.',
  lineItems: 'No line items found matching your filters, selections and groupings.',
  ads: (
    <div>
      <p>
        <strong>No ads found matching your filters, selections and groupings.</strong>
      </p>
      <p>To create a new ad, go to the Line Items tab, select a line item, and click Create Ad.</p>
    </div>
  ),
};

export default function TraffickingPage(): JSX.Element {
  const {
    drawerProps,
    modalProps,
    tables,
    isClearAllBadgeVisible,
    clearAllSearchResultsAndSelections,
    clearAdsIdsToQuery,
    applyCommonFilter,
    isFilterPanelOpen,
    openFilterPanel,
    closeFilterPanel,
    scrollToFilterId,
    openFilterPanelToFilter,
    clearScrollToFilterId,
    lineItemNavigationScroll,
    scheduleTableReload,
    isReloading,
    showSelectedOnly,
  } = useTraffickerState();

  const { applyFilter, filters, applyTableFilter } = useFiltersContext();
  const { adsVsmLineItemNavigationLinks } = useFeatureFlags();

  const reloadContextValue = useMemo(() => ({ scheduleTableReload }), [scheduleTableReload]);

  const selectTab = useCallback(
    (tabName: TraffickingTableName): void => applyFilter(TRAFFICKING_SEARCH_PARAMS.SELECTED_TAB, tabName),
    [applyFilter]
  );

  const actionButtonsConfig = useMemo(() => {
    return [
      {
        id: CLEAR_ALL_BUTTON_LABEL,
        tooltipClassName: element('tooltip'),
        tooltipLabel: CLEAR_SEARCH_RESULTS_AND_SELECTION_BUTTON_TOOLTIP,
        isShown: isClearAllBadgeVisible,
        component: <ClearAllButton label={CLEAR_ALL_BUTTON_LABEL} onClick={clearAllSearchResultsAndSelections} />,
      },
    ];
  }, [isClearAllBadgeVisible, clearAllSearchResultsAndSelections]);

  const tableNamesToTabs = (tableName: TraffickingTableName): JSX.Element => {
    const {
      error,
      loading,
      initialLoading,
      tableInstance,
      hasMore,
      total,
      onNext,
      keyboardOnNext,
      defaultLimit,
      setOffset,
      resetSelectedIdOffset,
      handleDeleteItems,
      handleToggleAllSelectedOff,
    } = tables[tableName];

    const { initialState, state } = tableInstance;
    const selectedRowsCount = Object.keys(initialState?.selectedRowIds || {}).length;

    const onPillClear = (e: React.MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      if (adsVsmLineItemNavigationLinks) {
        handleToggleAllSelectedOff();
      } else {
        state.selectedRowIds = {};
        resetSelectedIdOffset(tableName);
        applyTableFilter(tableName, 'selectedRowIds', []);

        tables.ads.tableInstance.state.selectedRowIds = {};
        applyTableFilter('ads', 'selectedRowIds', []);
        clearAdsIdsToQuery();
      }
    };

    const handleDeleteItemsFromTable = (ids: string[]): void => handleDeleteItems(ids, handleToggleAllSelectedOff);

    const selectedLineItems = tables.lineItems.tableInstance.selectedFlatRows.map((item) => item.original);

    const selectedAds = tables.ads.tableInstance.selectedFlatRows.map((item) => item.original);
    const selectedCampaigns = tables.campaigns.tableInstance.selectedFlatRows.map((item) => item.original);

    return (
      <Tab
        key={tableName}
        title={tableName}
        element={
          <span tabIndex={0} onKeyDown={(e): boolean | void => e.key === 'Enter' && selectTab(tableName)} role="tab">
            {`${_lowerCase(tableName)}`}&nbsp;
            {!initialLoading && (
              <span className={'rows-count'} data-testid="rows-count">
                {total}
              </span>
            )}
            {selectedRowsCount > 0 && (
              <SelectedItemsPill selectedRowsCount={selectedRowsCount} onClearFunction={onPillClear} />
            )}
          </span>
        }
        className={element('table-content')}
      >
        <TableActionBar
          tableInstance={tableInstance as TableInstance<EntityModel>}
          tableName={tableName}
          total={total}
          setOffset={setOffset}
          selectedLineItems={selectedLineItems}
          selectedAds={selectedAds}
          selectedCampaigns={selectedCampaigns}
          isSequenceViewEnabled={tables[tableName].isSequenceViewEnabled}
          toggleIsSequenceViewEnabled={tables[tableName].toggleIsSequenceViewEnabled}
          isShowSelectedOnlyEnabled={showSelectedOnly.isEnabled}
          handleDeleteItemsFromTable={handleDeleteItemsFromTable}
        />
        <TraffickingTable
          tableName={tableName}
          initialLoading={initialLoading}
          isReloading={isReloading}
          emptyMessage={emptyTableMessages[tableName]}
          errorMessage={error}
          loading={loading}
          tableInstance={tableInstance as TableInstance<EntityModel>}
          hasMore={hasMore}
          onNext={onNext}
          keyboardOnNext={keyboardOnNext}
          defaultLimit={defaultLimit}
          isColumnResizing={!!state.columnResizing.isResizingColumn}
          isSequenceViewEnabled={tables[tableName].isSequenceViewEnabled}
          navigationTargetId={tableName === 'lineItems' ? lineItemNavigationScroll.scrollTargetId : null}
          onNavigationScrollComplete={
            tableName === 'lineItems' ? lineItemNavigationScroll.clearScrollTarget : undefined
          }
        />
      </Tab>
    );
  };

  return (
    <TraffickingTableReloadContext.Provider value={reloadContextValue}>
      <div className={`${block()} page`}>
        <IconDrag id={draggedElementId} className={`${element('drag-element')}`} />
        <div className={element('trafficker')}>
          <TableFilterBar
            applyCommonFilter={applyCommonFilter}
            isFilterPanelOpen={isFilterPanelOpen}
            onOpenFilterPanel={openFilterPanel}
            onCloseFilterPanel={closeFilterPanel}
            scrollToFilterId={scrollToFilterId}
            clearScrollToFilterId={clearScrollToFilterId}
          />
          <AppliedFiltersBar applyCommonFilter={applyCommonFilter} onChipClick={openFilterPanelToFilter} />
          <div className={element('tab-navigation-wrapper')}>
            <TableNavigationActionButtons config={actionButtonsConfig} />
            {showSelectedOnly.isAvailable && (
              <ToggleSwitch
                className={element('hide-unselected-rows-toggle')}
                isHorizontalPositioning
                isOn={showSelectedOnly.isEnabled}
                onChange={showSelectedOnly.setIsEnabled}
                label={HIDE_UNSELECTED_ROWS_LABEL}
                toggleTooltip={HIDE_UNSELECTED_ROWS_TOOLTIP}
              />
            )}
            <TabNavigation selectedTab={filters?.selectedTab!} onTabClick={selectTab}>
              {TRAFFICKING_TABLE_NAMES.map(tableNamesToTabs)}
            </TabNavigation>
          </div>
        </div>
        {drawerProps.isOpen && <TraffickingPageDrawer {...drawerProps} />}
        {modalProps.isOpen && <CustomModal {...modalProps} />}
      </div>
    </TraffickingTableReloadContext.Provider>
  );
}
